import React, { useEffect, useState, useRef } from 'react';
import * as R from 'ramda';
import { Box } from '@material-ui/core';
import { green, grey, red } from '@material-ui/core/colors';
import {
  HelpOutline,
  SentimentDissatisfied,
  SentimentSatisfied,
  SentimentSatisfiedAlt,
  SentimentVeryDissatisfied,
  SentimentVerySatisfied,
} from '@material-ui/icons';
import { ResponsiveBar } from '@nivo/bar';
import { useGradeUpdates } from './GradeUpdatesContext';

const colorMap = {
  very_good: green[900],
  good: green[500],
  normal: grey[500],
  bad: red[500],
  very_bad: red[900],
  not_graded: '#BCB6E2',
};

const iconMap = {
  very_good: SentimentVerySatisfied,
  good: SentimentSatisfiedAlt,
  normal: SentimentSatisfied,
  bad: SentimentDissatisfied,
  very_bad: SentimentVeryDissatisfied,
  not_graded: HelpOutline,
};

const AssignmentGradeDistributionField = ({ record, source }) => {
  const deliveries = R.pathOr([], R.split('.', source), record);
  const { getGrade, gradeUpdates } = useGradeUpdates();
  const [graphData, setGraphData] = useState([]);
  const prevHashRef = useRef('');

  const updatedDeliveries = deliveries.map((d) => {
    const studentId = d?.id;
    const deliveryName = d?.name;
    const updatedGrade = getGrade(studentId, deliveryName, d?.grade);
    return { ...d, grade: updatedGrade };
  });

  const currentHash = JSON.stringify(
    updatedDeliveries.map((d) => ({ id: d?.id || d?.name, grade: d?.grade }))
  );

  useEffect(() => {
    if (currentHash !== prevHashRef.current) {
      const initialCounts = {
        very_good: 0,
        good: 0,
        normal: 0,
        bad: 0,
        very_bad: 0,
        not_graded: 0,
      };

      const newGraphData = [
        updatedDeliveries.reduce((acc, d) => {
          const grade = d?.grade || 'not_graded';
          acc[grade] = (acc[grade] || 0) + 1;
          return acc;
        }, initialCounts),
      ];

      setGraphData(newGraphData);
      prevHashRef.current = currentHash;
    }
  }, [currentHash, updatedDeliveries, gradeUpdates]);

  return (
    <Box height="40px" width="98%">
      <ResponsiveBar
        data={graphData}
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        keys={['very_good', 'good', 'normal', 'bad', 'very_bad', 'not_graded']}
        layout="horizontal"
        tooltip={({ id, color }) => {
          const Icon = iconMap[id];
          return <Icon style={{ color }} />;
        }}
        labelTextColor={R.pipe(
          R.propOr('', 'key'),
          R.ifElse(R.startsWith('very_'), R.always('white'), R.always('black'))
        )}
        colors={({ id }) => colorMap[id]}
      />
    </Box>
  );
};

AssignmentGradeDistributionField.defaultProps = {
  addLabel: true,
  label: 'Grades',
  source: 'deliveries',
};

export default AssignmentGradeDistributionField;
